import type {
  DocumentData,
  DocumentReference,
  Unsubscribe,
} from 'firebase/firestore'
import { doc, getDoc, onSnapshot, updateDoc } from 'firebase/firestore'
import { useEffect, useMemo, useRef, useState } from 'react'

import type { ProjectIdentity, ProjectSwitch, SessionKey } from '@store/types'

import {
  selectFromResult as selectFromProjectConfigResult,
  useLazyGetConfigQuery,
} from '@api/config'

import firestore from './firebase-db'

const MASTER_KEY_COLLECTION = 'master-key'

interface MasterKeyDocumentData {
  activeProject?: string
  id?: string
}

interface ResolveMasterKeyDocResult {
  ref: DocumentReference<DocumentData>
  data: MasterKeyDocumentData
}

const resolveMasterKeyDoc = async (
  masterKey: string
): Promise<ResolveMasterKeyDocResult | null> => {
  if (!firestore || !masterKey) {
    return null
  }

  try {
    const docRef = doc(firestore, MASTER_KEY_COLLECTION, masterKey)
    const docSnap = await getDoc(docRef)

    if (!docSnap.exists()) {
      return null
    }

    return {
      ref: docRef,
      data: docSnap.data() as MasterKeyDocumentData,
    }
  } catch (error) {
    console.error(
      `[resolveMasterKeyDoc] Failed to resolve master key document: ${masterKey}`,
      error
    )
    return null
  }
}

const subscribeToMasterKeyDoc = async (
  masterKey: string,
  currentProject: string,
  onChange: (activeProject: string) => void
): Promise<Unsubscribe | undefined> => {
  const resolved = await resolveMasterKeyDoc(masterKey)

  if (!resolved) {
    return undefined
  }

  return onSnapshot(
    resolved.ref,
    (snapshot) => {
      if (!snapshot.exists()) {
        onChange('')
        return undefined
      }

      const data = snapshot.data() as MasterKeyDocumentData
      const activeProject = data?.activeProject?.trim() || ''

      if (!activeProject && currentProject) {
        updateDoc(resolved.ref, {
          activeProject: currentProject,
        }).catch((error) => {
          console.error(
            `[subscribeToMasterKeyDoc] Failed to update activeProject to "${currentProject}" in master-key document`,
            error
          )
        })
        onChange(currentProject)
        return undefined
      }

      return onChange(activeProject)
    },
    (error) => {
      console.error(
        '[subscribeToMasterKeyDoc] Firebase listener error - failed to subscribe to master-key document changes',
        error
      )
      onChange('')
    }
  )
}

const resolveProjectSwitch = async (
  activeProject: string,
  currentProject: string,
  sessions: SessionKey[],
  getProjectDetails: (
    projectCanonicalName: string
  ) => Promise<{ projectId: string; projectLabel: string } | null>
): Promise<ProjectSwitch | null> => {
  if (!activeProject || activeProject === currentProject) {
    return null
  }

  if (!sessions.some((s) => s.project === activeProject)) {
    return null
  }

  const projectDetails = await getProjectDetails(activeProject)

  if (!projectDetails) {
    return null
  }

  return {
    projectName: activeProject,
    projectId: projectDetails.projectId,
    projectLabel: projectDetails.projectLabel,
  }
}

interface UseActiveProjectSubscriptionParams {
  projectIdentity: ProjectIdentity
  hasToken: boolean
  skip: boolean
  onProjectSwitch: (projectSwitch: ProjectSwitch) => void | Promise<void>
}

const useActiveProjectSubscription = ({
  projectIdentity,
  hasToken,
  skip,
  onProjectSwitch,
}: UseActiveProjectSubscriptionParams) => {
  const [getProjectConfig] = useLazyGetConfigQuery()
  const unsubscribeRef = useRef<Unsubscribe | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(false)

  const getProjectDetails = async (projectCanonicalName: string) => {
    try {
      const projectConfigResponse = await getProjectConfig({
        projectName: projectCanonicalName,
      })
      const { project } = selectFromProjectConfigResult(projectConfigResponse)

      if (!project?.uuid || !project?.title) {
        return null
      }

      return {
        projectId: project.uuid,
        projectLabel: project.title,
      }
    } catch (error) {
      console.error(
        `[useActiveProjectSubscription] Failed to fetch project details for "${projectCanonicalName}"`,
        error
      )
      return null
    }
  }

  const { masterKey, projectName, sessionId: sessions } = projectIdentity
  const sessionsSignature = useMemo(
    () =>
      sessions
        .map((session) => `${session.project}:${session.key}`)
        .sort()
        .join('|'),
    [sessions]
  )

  useEffect(() => {
    let isDisposed = false
    let hasProcessedInitialSnapshot = false
    let isResolvingSwitch = false

    const shouldSkip =
      !hasToken || skip || !masterKey || !projectName || !sessions.length

    if (shouldSkip) {
      setIsLoading(false)
      return undefined
    }

    setIsLoading(true)

    subscribeToMasterKeyDoc(masterKey, projectName, async (activeProject) => {
      const projectSwitch = await resolveProjectSwitch(
        activeProject,
        projectName,
        sessions,
        getProjectDetails
      )

      if (!hasProcessedInitialSnapshot) {
        hasProcessedInitialSnapshot = true

        if (!projectSwitch && !isDisposed) {
          setIsLoading(false)
        }

        if (!projectSwitch) {
          return
        }

        isResolvingSwitch = true

        Promise.resolve(onProjectSwitch(projectSwitch)).catch((error) => {
          console.error(
            `[useActiveProjectSubscription] Failed to process initial project switch to "${projectSwitch.projectName}" (projectId: ${projectSwitch.projectId})`,
            error
          )
          isResolvingSwitch = false
          if (!isDisposed) {
            setIsLoading(false)
          }
        })

        return
      }

      if (projectSwitch && !isResolvingSwitch) {
        isResolvingSwitch = true
        Promise.resolve(onProjectSwitch(projectSwitch)).catch((error) => {
          console.error(
            `[useActiveProjectSubscription] Failed to process project switch to "${projectSwitch.projectName}" (projectId: ${projectSwitch.projectId})`,
            error
          )
          isResolvingSwitch = false
        })
      }
    })
      .then((unsubscribe) => {
        if (isDisposed) {
          unsubscribe?.()
          return
        }

        unsubscribeRef.current = unsubscribe

        if (!unsubscribe && !hasProcessedInitialSnapshot) {
          setIsLoading(false)
        }
      })
      .catch((error) => {
        console.error(
          '[useActiveProjectSubscription] Failed to subscribe to master-key document',
          error
        )
        if (!isDisposed) {
          setIsLoading(false)
        }
      })

    return () => {
      isDisposed = true
      unsubscribeRef.current?.()
      unsubscribeRef.current = undefined
    }
  }, [hasToken, skip, masterKey, projectName, sessionsSignature])

  return isLoading
}

export default useActiveProjectSubscription
